export type AppLanguage = "en" | "zh";
export type AppTheme = "light" | "dark";

export type AppSettings = {
  language: AppLanguage;
  theme: AppTheme;
  siteTitle: string;
  headerTitle: string;
  headerSubtitle: string;
  faviconFileId: string | null;
  adminLogoFileId: string | null;
  faviconUrl: string | null;
  adminLogoUrl: string | null;
  adminLogoDarkFileId: string | null;
  adminLogoDarkUrl: string | null;
};

export const defaultAppSettings: AppSettings = {
  language: "en",
  theme: "light",
  siteTitle: "VZEN Quotation Workbench",
  headerTitle: "VZEN Quote",
  headerSubtitle: "Internal workbench",
  faviconFileId: null,
  adminLogoFileId: null,
  faviconUrl: null,
  adminLogoUrl: null,
  adminLogoDarkFileId: null,
  adminLogoDarkUrl: null,
};

const translations = {
  en: {
    "app.name": "VZEN Quote",
    "app.subtitle": "Internal workbench",
    "nav.dashboard": "Dashboard",
    "nav.quotations": "Quotations",
    "nav.settings": "Settings",
    "controls.language": "中文",
    "controls.themeDark": "Dark",
    "controls.themeLight": "Light",
    "auth.loading": "Checking session...",
    "auth.logout": "Log out",
    "dashboard.title": "Quotation Dashboard",
    "dashboard.description": "Create, review, publish, and export VZEN proposals.",
    "dashboard.newQuote": "New quote",
    "dashboard.totalPipeline": "Total pipeline",
    "dashboard.recentQuotations": "Recent quotations",
    "dashboard.loading": "Loading...",
    "dashboard.empty": "No quotations yet.",
    "dashboard.loadError": "Unable to load dashboard.",
    "dashboard.quotationCount": "quotations",
    "status.draft": "Draft",
    "status.internalReview": "Internal Review",
    "status.sent": "Sent",
    "status.approved": "Approved",
    "login.title": "Admin Login",
    "login.email": "Email",
    "login.password": "Password",
    "login.submit": "Sign in",
    "login.signingIn": "Signing in...",
    "login.failed": "Sign-in failed.",
    "quotations.title": "Quotations",
    "quotations.description": "Search and manage quotation versions.",
    "quotations.empty": "No quotations yet.",
    "quotations.loading": "Loading quotations...",
    "quotations.loadError": "Unable to load quotations.",
    "quotations.linkReady": "Ready",
    "quotations.linkNone": "None",
    "quoteNew.title": "Create Quote",
    "quoteNew.description": "Select a template, review modules, then generate line items.",
    "quoteNew.cardDescription": "Default modules and bilingual terms.",
    "quoteNew.select": "Select",
    "quoteNew.selected": "Selected",
    "quoteNew.projectTitle": "Project title",
    "quoteNew.projectTitlePlaceholder": "Client / campaign / deliverable",
    "quoteNew.modules": "Modules",
    "quoteNew.noTemplate": "Select a template first",
    "quoteNew.create": "Create quote",
    "quoteNew.creating": "Creating...",
    "quoteNew.loadError": "Unable to load quotation templates.",
    "quoteNew.submitError": "Unable to create quotation.",
    "quoteEdit.preview": "Preview",
    "quoteEdit.save": "Save",
    "quoteEdit.newVersion": "New version",
    "quoteEdit.creatingVersion": "Creating version...",
    "quoteEdit.versionError": "Unable to create a new version.",
    "quoteEdit.saving": "Saving...",
    "quoteEdit.saved": "Quotation saved.",
    "quoteEdit.loading": "Loading quotation...",
    "quoteEdit.loadError": "Unable to load quotation.",
    "quoteEdit.saveError": "Unable to save quotation.",
    "quoteEdit.notFound": "Quotation not found.",
    "quoteEdit.projectTitle": "Project title",
    "quoteEdit.status": "Status",
    "quoteEdit.validUntil": "Valid until",
    "quoteEdit.languageMode": "Language",
    "quoteEdit.financials": "Financials",
    "quoteEdit.taxMode": "Tax mode",
    "quoteEdit.taxRate": "Tax rate",
    "quoteEdit.discountType": "Discount type",
    "quoteEdit.discountValue": "Discount value",
    "quoteEdit.packagePrice": "Use package price",
    "quoteEdit.visualExport": "Visual & Export",
    "quoteEdit.confidentialLabel": "Confidential label",
    "quoteEdit.watermark": "Watermark",
    "quoteEdit.watermarkText": "Custom watermark text",
    "quoteEdit.fontSet": "Font set",
    "quoteEdit.pdf": "PDF",
    "quoteEdit.excel": "Excel",
    "quoteEdit.exporting": "Exporting...",
    "quoteEdit.exportError": "Unable to export quotation.",
    "quoteEdit.pdfReady": "PDF HTML export downloaded.",
    "quoteEdit.excelReady": "Excel export downloaded.",
    "quoteEdit.clientLink": "Client Link",
    "quoteEdit.linkPassword": "Link password",
    "quoteEdit.allowPdf": "Allow printing / Save as PDF",
    "quoteEdit.allowExcel": "Allow Excel download",
    "quoteEdit.generateLink": "Generate link",
    "quoteEdit.generatingLink": "Generating...",
    "quoteEdit.linkCreated": "Client link generated.",
    "quoteEdit.linkError": "Unable to generate client link.",
    "quotePreview.title": "Quote Preview",
    "quotePreview.description": "Client-facing web/PDF structure preview.",
    "quotePreview.edit": "Edit",
    "quotePreview.loading": "Loading preview...",
    "quotePreview.notFound": "Preview not found.",
    "quotePreview.loadError": "Unable to load preview.",
    "quotePreview.frameTitle": "Quotation PDF preview",
    "settings.title": "Settings",
    "settings.description": "Manage system defaults for the internal quote workbench.",
    "settings.siteTitle": "Website title",
    "settings.siteTitleHelp": "Shown in the browser tab title.",
    "settings.siteTitlePlaceholder": "VZEN Quotation Workbench",
    "settings.general": "General",
    "settings.users": "Users",
    "settings.usersDescription": "Administrator-managed internal staff and external collaborator accounts.",
    "settings.company": "Company",
    "settings.companyDescription": "VZEN company profile and logo defaults.",
    "settings.templates": "Templates",
    "settings.templatesDescription": "Quote templates, modules, and price library.",
    "settings.files": "Attachments",
    "settings.filesDescription": "Uploaded assets and generated exports.",
    "settings.fonts": "Fonts",
    "settings.fontsDescription": "Global font sets and uploaded commercial fonts.",
    "settings.terms": "Terms",
    "settings.termsDescription": "Reusable bilingual terms and payment templates.",
    "public.title": "Quotation Access",
    "public.description": "Enter the password provided by VZEN Studio.",
    "public.password": "Password",
    "public.view": "View quote",
    "public.loading": "Loading quotation...",
    "public.verifying": "Verifying...",
    "public.loadError": "Unable to load this quotation link.",
    "public.verifyError": "Unable to verify password.",
    "public.print": "Print / Save as PDF",
    "public.frameTitle": "Quotation preview",
    "public.validUntil": "Valid until",
    "public.terms": "Terms",
    "public.quoteTitle": "VZEN Quotation",
    "public.quoteDescription": "Read-only client view",
    "summary.title": "Summary",
    "summary.subtotal": "Subtotal",
    "summary.package": "Package Price",
    "summary.tax": "Tax",
    "summary.grandTotal": "Grand Total",
    "summary.note": "Valid until, tax mode, watermark, and terms will be loaded from quotation settings.",
  },
  zh: {
    "app.name": "VZEN 报价",
    "app.subtitle": "内部报价工作台",
    "nav.dashboard": "工作台",
    "nav.quotations": "报价单",
    "nav.settings": "设置",
    "controls.language": "EN",
    "controls.themeDark": "深色",
    "controls.themeLight": "浅色",
    "auth.loading": "正在检查登录状态...",
    "auth.logout": "退出",
    "dashboard.title": "报价工作台",
    "dashboard.description": "创建、审核、发布并导出 VZEN 报价。",
    "dashboard.newQuote": "新建报价",
    "dashboard.totalPipeline": "报价总额",
    "dashboard.recentQuotations": "最近报价",
    "dashboard.loading": "正在加载...",
    "dashboard.empty": "暂无报价单。",
    "dashboard.loadError": "无法加载工作台。",
    "dashboard.quotationCount": "份报价",
    "status.draft": "草稿",
    "status.internalReview": "内部审核",
    "status.sent": "已发送",
    "status.approved": "已通过",
    "login.title": "后台登录",
    "login.email": "邮箱",
    "login.password": "密码",
    "login.submit": "登录",
    "login.signingIn": "登录中...",
    "login.failed": "登录失败。",
    "quotations.title": "报价单",
    "quotations.description": "搜索和管理报价版本。",
    "quotations.empty": "暂无报价单。",
    "quotations.loading": "正在加载报价单...",
    "quotations.loadError": "无法加载报价单。",
    "quotations.linkReady": "已生成",
    "quotations.linkNone": "未生成",
    "quoteNew.title": "创建报价",
    "quoteNew.description": "选择模板，确认模块，然后生成报价明细。",
    "quoteNew.cardDescription": "默认模块与双语条款。",
    "quoteNew.select": "选择",
    "quoteNew.selected": "已选择",
    "quoteNew.projectTitle": "项目标题",
    "quoteNew.projectTitlePlaceholder": "客户 / 活动 / 交付内容",
    "quoteNew.modules": "报价模块",
    "quoteNew.noTemplate": "请先选择模板",
    "quoteNew.create": "创建报价",
    "quoteNew.creating": "创建中...",
    "quoteNew.loadError": "无法加载报价模板。",
    "quoteNew.submitError": "无法创建报价。",
    "quoteEdit.preview": "预览",
    "quoteEdit.save": "保存",
    "quoteEdit.newVersion": "新版本",
    "quoteEdit.creatingVersion": "正在创建版本...",
    "quoteEdit.versionError": "无法创建新版本。",
    "quoteEdit.saving": "保存中...",
    "quoteEdit.saved": "报价已保存。",
    "quoteEdit.loading": "正在加载报价...",
    "quoteEdit.loadError": "无法加载报价。",
    "quoteEdit.saveError": "无法保存报价。",
    "quoteEdit.notFound": "未找到报价。",
    "quoteEdit.projectTitle": "项目标题",
    "quoteEdit.status": "状态",
    "quoteEdit.validUntil": "有效期",
    "quoteEdit.languageMode": "语言",
    "quoteEdit.financials": "财务设置",
    "quoteEdit.taxMode": "税务模式",
    "quoteEdit.taxRate": "税率",
    "quoteEdit.discountType": "折扣类型",
    "quoteEdit.discountValue": "折扣数值",
    "quoteEdit.packagePrice": "启用打包价",
    "quoteEdit.visualExport": "视觉与导出",
    "quoteEdit.confidentialLabel": "保密标签",
    "quoteEdit.watermark": "水印",
    "quoteEdit.watermarkText": "自定义水印文字",
    "quoteEdit.fontSet": "字体组合",
    "quoteEdit.pdf": "PDF",
    "quoteEdit.excel": "Excel",
    "quoteEdit.exporting": "导出中...",
    "quoteEdit.exportError": "无法导出报价。",
    "quoteEdit.pdfReady": "PDF HTML 已下载。",
    "quoteEdit.excelReady": "Excel 已下载。",
    "quoteEdit.clientLink": "客户链接",
    "quoteEdit.linkPassword": "链接密码",
    "quoteEdit.allowPdf": "允许打印 / 另存为 PDF",
    "quoteEdit.allowExcel": "允许下载 Excel",
    "quoteEdit.generateLink": "生成链接",
    "quoteEdit.generatingLink": "生成中...",
    "quoteEdit.linkCreated": "客户链接已生成。",
    "quoteEdit.linkError": "无法生成客户链接。",
    "quotePreview.title": "报价预览",
    "quotePreview.description": "面向客户的网页/PDF 结构预览。",
    "quotePreview.edit": "编辑",
    "quotePreview.loading": "正在加载预览...",
    "quotePreview.notFound": "未找到报价预览。",
    "quotePreview.loadError": "无法加载报价预览。",
    "quotePreview.frameTitle": "报价 PDF 预览",
    "settings.title": "设置",
    "settings.description": "管理内部报价工作台的系统默认项。",
    "settings.siteTitle": "网站标题",
    "settings.siteTitleHelp": "显示在浏览器标签页标题中。",
    "settings.siteTitlePlaceholder": "VZEN 在线报价系统",
    "settings.general": "通用",
    "settings.users": "用户",
    "settings.usersDescription": "管理员创建的公司内部及外部协作账号。",
    "settings.company": "公司",
    "settings.companyDescription": "VZEN 公司资料和默认 logo。",
    "settings.templates": "模板",
    "settings.templatesDescription": "报价模板、模块和价格项库。",
    "settings.files": "附件",
    "settings.filesDescription": "上传资源与生成的导出文件。",
    "settings.fonts": "字体",
    "settings.fontsDescription": "全局字体组合和上传字体。",
    "settings.terms": "条款",
    "settings.termsDescription": "可复用的双语条款和付款模板。",
    "public.title": "报价访问",
    "public.description": "请输入 VZEN Studio 提供的访问密码。",
    "public.password": "密码",
    "public.view": "查看报价",
    "public.loading": "正在加载报价...",
    "public.verifying": "验证中...",
    "public.loadError": "无法加载此报价链接。",
    "public.verifyError": "无法验证密码。",
    "public.print": "打印 / 另存为 PDF",
    "public.frameTitle": "报价预览",
    "public.validUntil": "有效期至",
    "public.terms": "条款",
    "public.quoteTitle": "VZEN 报价单",
    "public.quoteDescription": "客户只读视图",
    "summary.title": "汇总",
    "summary.subtotal": "明细合计",
    "summary.package": "打包价",
    "summary.tax": "税金",
    "summary.grandTotal": "总计",
    "summary.note": "有效期、税务模式、水印和条款将从报价设置中加载。",
  },
} as const;

export type TranslationKey = keyof typeof translations.en;

export function isLanguage(value: unknown): value is AppLanguage {
  return value === "en" || value === "zh";
}

export function isTheme(value: unknown): value is AppTheme {
  return value === "light" || value === "dark";
}

export function translate(key: string, language: AppLanguage): string {
  if (isTranslationKey(key)) {
    return translations[language][key] ?? translations.en[key];
  }

  return key;
}

export function normalizeSiteTitle(value: string): string {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : defaultAppSettings.siteTitle;
}

export function getPageTitle(pageTitle: string, siteTitle: string): string {
  return `${pageTitle} | ${normalizeSiteTitle(siteTitle)}`;
}

function isTranslationKey(key: string): key is TranslationKey {
  return key in translations.en;
}
